import { useRef } from 'react'


/**
 * Passes `value` straight through until `isFrozen` first becomes true,
 * and returns the value it held at that moment for the rest of the
 * component's life.
 *
 * The capture happens during render rather than in an effect because
 * the caller's reason for freezing is usually the render itself: by the
 * time an effect ran, the value it meant to pin down could already have
 * been painted and replaced.
 *
 * Note that the value is held by reference. That is sound only for as
 * long as nobody mutates it in place — callers are expected to replace
 * such a value rather than edit it. Should that ever stop being true,
 * this hook has to capture a copy instead.
 *
 * @param {Object} args
 * @param {Boolean} [args.isFrozen]
 * @param {*} [args.value]
 *
 * @returns {*}
 */
export default function useFrozenValue({ isFrozen, value }) {
  // The capture is wrapped so that having frozen an `undefined` value
  // stays distinguishable from never having frozen at all.
  const frozen = useRef()

  if ( isFrozen && !frozen.current ) {
    frozen.current = { value }
  }

  if ( frozen.current ) {
    return frozen.current.value
  }

  return value
}
